/**
 * `useCreateCustomArt` — TH-53369 / M5.E6.S2.2.
 *
 * Wrapper TanStack Query do POST /custom-arts. Mantemos a chamada
 * fora do componente do modal pra (a) facilitar mock em testes e
 * (b) reaproveitar em outras telas (queue, gerência por loja) sem
 * repetir o boilerplate de FormData.
 *
 * Backend (TH-53365) aceita multipart com `file` + metadata opcional
 * e devolve 201 com `{art, warnings[]}`. Warnings hoje só carregam
 * aspect ratio fora de 16:9 — não bloqueiam o POST.
 */

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

export interface CustomArtRead {
  id: string;
  tenant_id: string | null;
  store_id: string | null;
  area_id: string | null;
  uploaded_by: string;
  name: string;
  art_url: string;
  width: number;
  height: number;
  file_size_bytes: number;
  mime_type: string;
  active: boolean;
  scope: "global" | "tenant" | "supplier";
  supplier_id: string | null;
  supplier_name: string | null;
  origin_label: string;
  // Geração via IA (TH-55363). Para uploads esses campos vêm todos null
  // e source='upload'; para artes IA, generation_status acompanha o
  // pipeline pending → generating → ready|failed.
  source?: "upload" | "ai_generated";
  generation_status?: "pending" | "generating" | "ready" | "failed" | null;
  ai_prompt?: string | null;
  ai_model_id?: string | null;
  ai_category?: string | null;
  ai_cost_brl?: string | null;
  ai_error_message?: string | null;
  product_id?: string | null;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomArtUploadResponse {
  art: CustomArtRead;
  warnings: string[];
}

export interface CreateCustomArtInput {
  file: File;
  name: string;
  store_id?: string;
  area_id?: string;
  valid_from?: string;
  valid_until?: string;
}

async function postCustomArt(
  input: CreateCustomArtInput,
): Promise<CustomArtUploadResponse> {
  const fd = new FormData();
  fd.append("file", input.file);
  fd.append("name", input.name);
  if (input.store_id) fd.append("store_id", input.store_id);
  if (input.area_id) fd.append("area_id", input.area_id);
  if (input.valid_from) fd.append("valid_from", input.valid_from);
  if (input.valid_until) fd.append("valid_until", input.valid_until);

  const res = await fetch("/api/custom-arts", {
    method: "POST",
    credentials: "include",
    body: fd,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }

  return (await res.json()) as CustomArtUploadResponse;
}

export function useCreateCustomArt(): UseMutationResult<
  CustomArtUploadResponse,
  Error,
  CreateCustomArtInput
> {
  return useMutation({
    mutationFn: postCustomArt,
  });
}
